import type { ComponentType } from 'react'
import { useEffect, useState } from 'react'
import { PLACE_ID_ROLE } from '../../places/places'
import { registerWidget, type CellContentProps, type CellEditorProps } from '../registry'
import { InlineEditor } from '../InlineEditor'
import { formatDate, getLocale } from '../../locale'
import { renderInline } from '../../dashboards/markdownLite'

/** E3 · text content — the value as a string, em-dash for empty. Content only. Inline **bold** / `code` in a
 *  stored text is shown as emphasis through the one markdown renderer, never as literal asterisks (SEN-439 X5). */
function TextContent({ value }: CellContentProps) {
  const text = value == null ? '' : String(value)
  return text ? <>{/\*\*|`/.test(text) ? renderInline(text) : text}</> : <span className="text-muted-foreground">—</span>
}

/** The time now in IANA zone `zone` ("14:05"), or null when `zone` is not a zone. */
export function localTimeIn(zone: string, now: Date = new Date(), locale: string = getLocale()): string | null {
  try {
    return formatDate(now, 'time', { zone, locale })
  } catch {
    return null
  }
}

/** 2026-10-23 · text content, `local_time` variant — a text holding an IANA
 *  zone read as the time there now (a site's local time), with the zone
 *  beside it. Ticks each minute. Not a zone: the plain value (annotate, never
 *  blank). */
function LocalTimeContent(props: CellContentProps) {
  const zone = props.value == null ? '' : String(props.value)
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000)
    return () => clearInterval(t)
  }, [])
  const time = zone ? localTimeIn(zone, now) : null
  if (!time) return <TextContent {...props} />
  return (
    <span data-testid="cell-format-local_time">
      <span className="tabular-nums">{time}</span>
      <span className="ml-1.5 text-muted-foreground">{zone}</span>
    </span>
  )
}

/** The kind's format dispatcher (the date kind's shape): the declared variant
 *  picks the content renderer; an unknown variant draws the default. */
const TEXT_VARIANTS: Record<string, ComponentType<CellContentProps>> = {
  local_time: LocalTimeContent,
}

function TextCell(props: CellContentProps) {
  const Variant = props.format ? TEXT_VARIANTS[props.format] : undefined
  return Variant ? <Variant {...props} /> : <TextContent {...props} />
}

/** Text values can be long prose: in a table cell the editor expands to show
 *  the whole value (SEN-197) instead of a one-line box scrolled to its end. */
function TextEditor(props: CellEditorProps) {
  return <InlineEditor {...props} wrap />
}

registerWidget('text', {
  read: TextCell,
  write: TextEditor,
  defaultWidth: 240,
  formats: [
    { value: 'text', label: 'Text' },
    { value: 'local_time', label: 'Local time' },
  ],
  // A picked place names the record (the primary attribute) and fills the
  // attribute declared to hold its Google place id (`semantic: place_id`).
  fromPlace: (place, { primary, semantic }) =>
    semantic === PLACE_ID_ROLE ? place.place_id : primary ? place.name : undefined,
})
